import { Empty, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type {
  FilterValue,
  SortOrder,
  SorterResult,
} from 'antd/es/table/interface';
import dayjs from 'dayjs';

import type { ExtranetReview } from 'app/typings/core/extranet/reviews/reviews';

import styles from '../ExtranetReviews.module.scss';

import { ExtranetReviewsHeader } from './ExtranetReviewsHeader';

type FilterUpdates = Partial<{
  pageNumber: number;
  search: string;
  beginning: string;
  ending: string;
  sortingColumn: string;
  sortingOrder: 'ASC' | 'DESC';
}>;

type ExtranetReviewsContentProps = {
  reviews?: ExtranetReview[];
  isLoading: boolean;
  filters: FilterUpdates;
  updateFilters: (updates: FilterUpdates) => void;
  pagination?: {
    pageNumber: number;
    pageSize: number;
    pageCount: number;
    itemCount: number;
  };
  searchInput: string;
  setSearchInput: (value: string) => void;
  handleDateChange: (startDate?: string, endDate?: string) => void;
  isMultiPack: boolean;
  packIdsValue: number[];
  setPackIdsValue: (value: number[]) => void;
  packFilterOptions: { text: string; value: number }[];
};

export const ExtranetReviewsContent = ({
  reviews,
  isLoading,
  filters,
  updateFilters,
  pagination,
  searchInput,
  setSearchInput,
  handleDateChange,
  isMultiPack,
  packIdsValue,
  setPackIdsValue,
  packFilterOptions,
}: ExtranetReviewsContentProps) => {
  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>, // ← renommé
    sorter: SorterResult<ExtranetReview> | SorterResult<ExtranetReview>[]
  ) => {
    const sortConfig = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortingOrder = sortConfig.order
      ? sortConfig.order === 'ascend'
        ? 'ASC'
        : 'DESC'
      : undefined;

    const packIdFilter = tableFilters.nameForHotel as number[] | null;
    const allPackIds = packFilterOptions.map((opt) => opt.value);

    setPackIdsValue(packIdFilter ?? allPackIds);

    updateFilters({
      pageNumber: paginationConfig.current,
      sortingColumn: sortConfig.field ? String(sortConfig.field) : undefined,
      sortingOrder,
    });
  };

  const toAntdSortOrder = (
    order: 'ASC' | 'DESC' | undefined
  ): SortOrder | undefined => {
    if (!order) {
      return undefined;
    }

    return order === 'ASC' ? 'ascend' : 'descend';
  };

  const grayCell = (title: string) => (
    <div className={styles.grayHeader}>{title}</div>
  );

  const backendToFrontendColumn: Record<string, string> = {
    CREATED_AT: 'createdAt',
    GLOBAL_SCORE: 'globalScore',
  };

  const columns: ColumnsType<ExtranetReview> = [
    {
      title: 'Guest',
      key: 'customerName',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      width: 200,
    },
    ...(isMultiPack
      ? [
          {
            title: 'Package',
            dataIndex: 'nameForHotel',
            key: 'nameForHotel',
            filters: packFilterOptions,
            filteredValue: packIdsValue,
            onFilter: () => true,
          },
        ]
      : []),
    {
      title: 'Published',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder:
        filters.sortingColumn &&
        backendToFrontendColumn[filters.sortingColumn] === 'createdAt'
          ? toAntdSortOrder(filters.sortingOrder)
          : undefined,
      render: (createdAt) => dayjs(createdAt).format('DD MMM YYYY'),
      width: 120,
    },
    {
      title: 'Global',
      dataIndex: 'globalScore',
      key: 'globalScore',
      sorter: true,
      align: 'center',
      render: (score) => {
        const numericScore = Number(score);

        return !isNaN(numericScore) ? numericScore.toFixed(1) : '-';
      },
      sortOrder:
        filters.sortingColumn &&
        backendToFrontendColumn[filters.sortingColumn] === 'globalScore'
          ? toAntdSortOrder(filters.sortingOrder)
          : undefined,
      width: 90,
    },
    {
      title: grayCell('Experience'),
      dataIndex: 'experienceScore',
      key: 'experienceScore',
      render: grayCell,
      width: 90,
    },
    {
      title: grayCell('Service'),
      dataIndex: 'serviceScore',
      key: 'serviceScore',
      render: grayCell,
      width: 70,
    },
    {
      title: grayCell('Vibe'),
      dataIndex: 'vibeScore',
      key: 'vibeScore',
      render: grayCell,
      width: 50,
    },
    {
      title: grayCell('Room'),
      dataIndex: 'roomScore',
      key: 'roomScore',
      render: grayCell,
      width: 60,
    },
    {
      title: grayCell('Breakfast'),
      dataIndex: 'breakfastScore',
      key: 'breakfastScore',
      render: grayCell,
      width: 90,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      width: 330,
    },
  ];

  return (
    <Table<ExtranetReview>
      title={() => (
        <ExtranetReviewsHeader
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          beginning={filters.beginning}
          ending={filters.ending}
          onDateChange={handleDateChange}
          pagination={pagination}
          onPageChange={(page) => updateFilters({ pageNumber: page })}
        />
      )}
      columns={columns}
      dataSource={reviews}
      scroll={{ x: 'max-content' }}
      loading={{
        spinning: isLoading,
      }}
      onChange={handleTableChange}
      pagination={{
        current: pagination?.pageNumber,
        pageSize: pagination?.pageSize,
        total: pagination?.itemCount,
        showSizeChanger: false,
        simple: true,
        position: ['bottomRight'],
      }}
      sticky
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No review found"
          />
        ),
      }}
    />
  );
};
